import type { Application } from '@hotwired/stimulus';
import formFields from './form-fields/form-fields';
import menus from './menus';
import alerts from './alerts/alerts';
import cart from './shopping-cart/shopping-cart';
import cartWidget from './shopping-cart/shopping-cart-widget';
import cards from './cards/cards';
import eventInfoCard from './cards/event-info-card';
import textExpand from './text-expand/text-expand';
import tooltips from './tooltips/tooltips';
import typeahead from './typeahead/typeahead';
import video from './video/video';
import searchAutocomplete from './search-autocomplete/search-autocomplete';
import contentSlider from './content-slider/content-slider';
import { InlineEditController } from './inline-edit/inline-edit.stimulus';
import { ImageZoomController } from './images/image-zoom.stimulus';
import { FilterController } from './filters/filters.stimulus';
import { ProductItemOwnedController } from './products/product-item-owned.stimulus';
import { FormFieldsController } from './form-fields/form-fields.stimulus';
import { KeypadController } from './keypad/keypad.stimulus';
import { ScannerController } from './scanner/scanner.stimulus';
import { TesseractScanner } from './scanner/tesseract-scanner.stimulus';
import { shouldLoad as shouldFileInputFieldLoad } from './form-fields/file-input-field-loader';
import { shouldLoad as shouldTablistLoad } from './tablist/tablist-loader';
import { shouldLoad as shouldHorizontalChipFilterLoad } from './filters/horizontal-chip-filter-loader';
import { BrowserSupportAlertController } from './alerts/browser-support-alert.stimulus';
import { shouldLoad as shouldCardControllerLoad } from './cards/card-loader';
import { shouldLoad as shouldContentMarkerLoad } from './content-marker/content-marker-loader';
import { shouldLoad as shouldCardActionSelectionLoad } from './cards/card-action-selection-loader';
import { shouldLoad as shouldPasswordStrengthControllerLoad } from './form-fields/password-strength-form-field-loader';
import { shouldLoad as shouldHorizontalChipFilterPriorityLoad } from './filters/horizontal-chip-filter-priority-loader';
import { shouldLoad as shouldTextExpandLoad } from './text-expand/text-expand-loader';
import { shouldLoad as shouldExpandableControllerLoad } from './expandable/expandable-loader';
import { shouldLoad as shouldPaginationLoad } from './pagination/pagination-loader';

// v2
import { shouldLoad as shouldAccordionLoad } from './v2/accordion/accordion--loader';
import { shouldLoad as shouldCardLoad } from './v2/card/card--loader';
import { shouldLoad as shouldContentArchiveLoad } from './v2/content-blocks/content-archive/content-archive--loader';
import { shouldLoad as shouldEventDatesLoad } from './v2/event-dates/event-dates--loader';
import { shouldLoad as shouldFormLoad } from './v2/form/form--loader';
import { shouldLoad as shouldInputLoad } from './v2/inputs/inputs--loader';
import { shouldLoad as shouldInputSingleCharactersLoad } from './v2/inputs/single-characters/single-characters--loader';
import { shouldLoad as shouldOverlayMenuLoad } from './v2/overlay-menu/overlay-menu--loader';
import { shouldLoad as shouldSliderNavigationLoad } from './v2/slider-navigation/slider-navigation--loader';
import { shouldLoad as shouldTableHeadMultiSelectLoad } from './v2/table-cell/table-head-multi-select/table-head-multi-select--loader';
import { shouldLoad as shouldTableMultiSelectLoad } from './v2/table-cell/table-multi-select/table-multi-select--loader';
import { shouldLoad as shouldTableSelectLoad } from './v2/table-cell/table-select/table-select--loader';
import { shouldLoad as shouldTableUserInfoLoad } from './v2/table-cell/table-user-info/table-user-info--loader';
import { shouldLoad as shouldUserMenuLoad } from './v2/user-menu/user-menu--loader';

/**
 * Initializes all components
 */
export default async function init(vendorRoot: string, application: Application) {
    formFields(vendorRoot);
    menus();

    alerts();
    cards();
    cart();
    cartWidget();
    eventInfoCard();
    tooltips();
    textExpand();
    typeahead({ vendorRoot });
    searchAutocomplete();
    video(vendorRoot);
    contentSlider();

    BrowserSupportAlertController.register(application);
    InlineEditController.register(application);
    ImageZoomController.register(application);
    FilterController.register(application);
    ProductItemOwnedController.register(application);
    FormFieldsController.register(application);
    KeypadController.register(application);
    ScannerController.register(application);

    TesseractScanner.vendorRoot = vendorRoot;
    TesseractScanner.register(application);

    if (shouldFileInputFieldLoad()) {
        const { FileInputFieldController } = await import('../100-components/form-fields/file-input-field.stimulus');
        FileInputFieldController.register(application);
    }

    if (shouldTablistLoad()) {
        const { TablistController } = await import('../100-components/tablist/tablist.stimulus');
        TablistController.register(application);
    }

    if (shouldHorizontalChipFilterLoad()) {
        const { HorizontalChipFilterController } = await import('../100-components/filters/horizontal-chip-filter.stimulus');
        HorizontalChipFilterController.register(application);
    }

    if (shouldCardControllerLoad()) {
        const { CardController } = await import('../100-components/cards/card.stimulus');
        CardController.register(application);
    }

    if (shouldContentMarkerLoad()) {
        const { ContentMarkerController } = await import('../100-components/content-marker/content-marker.stimulus');
        ContentMarkerController.register(application);
    }

    if (shouldCardActionSelectionLoad()) {
        const { CardActionSelectionController } = await import('./cards/card-action-selection.stimulus');
        CardActionSelectionController.register(application);
    }

    if (shouldTextExpandLoad()) {
        const { TextExpandController } = await import('./text-expand/text-expand.stimulus');
        TextExpandController.register(application);
    }

    if (shouldPasswordStrengthControllerLoad()) {
        const { PasswordStrengthFormFieldController } = await import('./form-fields/password-strength-form-field.stimulus');
        PasswordStrengthFormFieldController.register(application);
    }

    if (shouldExpandableControllerLoad()) {
        const { ExpandableController } = await import('../100-components/expandable/expandable.stimulus');
        ExpandableController.register(application);
    }

    if (shouldPaginationLoad()) {
        const { PaginationController } = await import('../100-components/pagination/pagination.stimulus');
        PaginationController.register(application);
    }

    if (shouldHorizontalChipFilterPriorityLoad()) {
        const { HorizontalChipFilterPriorityController } = await import('./filters/horizontal-chip-filter-priority.stimulus');
        HorizontalChipFilterPriorityController.register(application);
    }

    // v2
    if (shouldAccordionLoad()) {
        const { AccordionController } = await import('./v2/accordion/accordion.stimulus');
        AccordionController.register(application);
    }

    if (shouldCardLoad()) {
        const { CardContainerController } = await import('./v2/card/card.stimulus');
        CardContainerController.register(application);
    }

    if (shouldContentArchiveLoad()) {
        const { ContentArchiveController } = await import('./v2/content-blocks/content-archive/content-archive.stimulus');
        ContentArchiveController.register(application);
    }

    if (shouldEventDatesLoad()) {
        const { EventDatesController } = await import('./v2/event-dates/event-dates.stimulus');
        EventDatesController.register(application);
    }

    if (shouldFormLoad()) {
        const { FormController } = await import('./v2/form/form.stimulus');
        FormController.register(application);
    }

    if (shouldInputLoad()) {
        const { InputController } = await import('./v2/inputs/inputs.stimulus');
        InputController.register(application);
    }

    if (shouldInputSingleCharactersLoad()) {
        const { InputSingleCharactersController } = await import('./v2/inputs/single-characters/single-characters.stimulus');
        InputSingleCharactersController.register(application);
    }

    if (shouldOverlayMenuLoad()) {
        const { OverlayMenuController } = await import('./v2/overlay-menu/overlay-menu.stimulus');
        OverlayMenuController.register(application);
    }

    if (shouldSliderNavigationLoad()) {
        const { SliderNavigationController } = await import('./v2/slider-navigation/slider-navigation.stimulus');
        SliderNavigationController.register(application);
    }

    if (shouldTableHeadMultiSelectLoad()) {
        const { TableHeadMultiSelectController } = await import('./v2/table-cell/table-head-multi-select/table-head-multi-select.stimulus');
        TableHeadMultiSelectController.register(application);
    }

    if (shouldTableMultiSelectLoad()) {
        const { TableMultiSelectController } = await import('./v2/table-cell/table-multi-select/table-multi-select.stimulus');
        TableMultiSelectController.register(application);
    }

    if (shouldTableSelectLoad()) {
        const { TableSelectController } = await import('./v2/table-cell/table-select/table-select.stimulus');
        TableSelectController.register(application);
    }

    if (shouldTableUserInfoLoad()) {
        const { TableUserInfoController } = await import('./v2/table-cell/table-user-info/table-user-info.stimulus');
        TableUserInfoController.register(application);
    }

    if (shouldUserMenuLoad()) {
        const { UserMenuController } = await import('./v2/user-menu/user-menu.stimulus');
        UserMenuController.register(application);
    }
}
